import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Field, SaveBar, fieldErrors } from "./Field";

// One form for Business Profile / Address & Contact / Tax — writes to the single source of truth
// (tenant name/colour + tenant-level invoice profile). Issued invoices keep their own snapshots.
export function ProfileForm({ id, fields, data, canEdit, onDirty }) {
  const qc = useQueryClient();
  const initial = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, f.from === "tenant" ? data.tenant[f.key] ?? "" : data.profile[f.key] ?? (f.type === "switch" ? false : "")])), [fields, data]);
  const [form, setForm] = useState(initial);
  const [errs, setErrs] = useState({});
  useEffect(() => { setForm(initial); setErrs({}); }, [initial]);
  const dirty = fields.some((f) => form[f.key] !== initial[f.key]);
  useEffect(() => { onDirty(id, dirty); }, [id, dirty, onDirty]);
  const save = useMutation({
    mutationFn: async () => (await api.put("/business/profile", Object.fromEntries(fields.filter((f) => form[f.key] !== initial[f.key]).map((f) => [f.key, form[f.key]])))).data,
    onSuccess: (r) => { setErrs({}); qc.setQueryData(["business-settings"], r); qc.invalidateQueries({ queryKey: ["tenant"] }); toast.success(r.changed?.length ? "Business details saved" : "No changes"); },
    onError: (e) => { setErrs(fieldErrors(e)); toast.error(apiErr(e)); },
  });
  return (
    <div data-testid={`${id}-form`}>
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map((f) => f.type === "switch" ? (
          <label key={f.key} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-sm sm:col-span-2">
            <Switch checked={!!form[f.key]} disabled={!canEdit} onCheckedChange={(v) => setForm({ ...form, [f.key]: v })} data-testid={`bs-${f.key}`} />{f.label}
          </label>
        ) : (
          <div key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
            <Field id={f.key} label={f.label} value={form[f.key]} type={f.type} placeholder={f.placeholder} hint={f.hint} disabled={!canEdit}
              error={errs[f.key]} onChange={(v) => setForm({ ...form, [f.key]: f.upper ? v.toUpperCase() : v })} />
          </div>
        ))}
      </div>
      {canEdit ? <SaveBar id={id} dirty={dirty} busy={save.isPending} onSave={() => save.mutate()} onCancel={() => { setForm(initial); setErrs({}); }} />
        : <p className="mt-4 text-xs text-slate-400" data-testid={`${id}-readonly`}>You can view these details. Ask an owner or admin to change them.</p>}
    </div>
  );
}

export const PROFILE_FIELDS = [
  { key: "name", label: "Business / display name", from: "tenant", wide: true },
  { key: "legal_name", label: "Legal business name", hint: "Shown on invoices", wide: true },
  { key: "brand_color", label: "Brand colour", from: "tenant", placeholder: "#059669" },
];
export const CONTACT_FIELDS = [
  { key: "phone", label: "Phone", type: "tel" }, { key: "email", label: "Email", type: "email" },
  { key: "website", label: "Website", placeholder: "https://", wide: true },
  { key: "address", label: "Address line 1", wide: true }, { key: "address_line2", label: "Address line 2", wide: true },
  { key: "locality", label: "Locality / area" }, { key: "city", label: "City" }, { key: "state", label: "State" },
  { key: "pin", label: "PIN / postal code" }, { key: "country", label: "Country", placeholder: "India" },
];
export const TAX_FIELDS = [
  { key: "gst_registered", label: "This business is GST registered", type: "switch" },
  { key: "gstin", label: "GSTIN", upper: true, placeholder: "22AAAAA0000A1Z5" },
  { key: "pan", label: "PAN", upper: true, placeholder: "AAAAA0000A" },
];
